import { useState } from 'react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SortAscendingIcon, TrashIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookColumnarSheet } from '@erp/shared-types';
import type { SheetPlan } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import type { ChangeSheet } from './useWorkbookEditor';
import { WorksheetReorderHandle } from './WorksheetReorderHandle';
import { useReorderSensors } from './useReorderSensors';
import {
  useSortDirectionLabel,
  worksheetFieldType,
} from './worksheetTypeLabels';
import { IconTooltip } from '@/components/common/IconTooltip';
import { moveSortKey, type SortKey } from './worksheetSortKeys';

/**
 * The Sort badge: in the grid's corner cell, while the Worksheet has a sort,
 * a sort icon with the number of sort keys. Hover or click opens the Sort
 * order to reorder (drag, or arrow keys on a key's handle), flip and remove
 * keys; column header menus add them. Sort order matters, unlike filters', so it lives here, once.
 */
export function WorksheetSortBadge({
  sheet,
  sheetPlan,
  readOnly,
  change,
}: {
  sheet: WorkbookColumnarSheet;
  sheetPlan?: SheetPlan;
  readOnly: boolean;
  change: ChangeSheet;
}) {
  const gt = useGT();
  const count = sheet.sort.length;
  const label =
    count === 1
      ? gt('Sort order: 1 column', { context: 'worksheet sort badge' })
      : gt('Sort order: {count} columns', {
          context: 'worksheet sort badge',
          count: String(count),
        });
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={300}
        render={
          // The trigger takes its content from `render`, not children.
          <button
            type="button"
            aria-label={label}
            title={label}
            className="relative inline-flex size-6 items-center justify-center rounded text-foreground hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
          >
            <SortAscendingIcon size={14} aria-hidden="true" />
            <span
              aria-hidden="true"
              className="absolute -right-1 -top-1 min-w-3.5 rounded-full bg-primary px-0.5 text-[9px] leading-3.5 font-semibold text-primary-foreground tabular-nums"
            >
              {count}
            </span>
          </button>
        }
      />
      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] space-y-2 p-3"
        aria-label={gt('Sort order', { context: 'worksheet sort order' })}
      >
        <h4 className="text-sm font-medium">
          <T context="worksheet sort order">Sort order</T>
        </h4>
        <p className="text-xs text-muted-foreground">
          <T context="worksheet sort precedence">
            Rows with the same value are ordered by the next sort down.
            Otherwise they keep their original order.
          </T>
        </p>
        <fieldset disabled={readOnly} className="space-y-2">
          <WorksheetSortOrder
            sheet={sheet}
            sheetPlan={sheetPlan}
            change={change}
          />
        </fieldset>
      </PopoverContent>
    </Popover>
  );
}

/** The Sort order's keys, first to last. */
export function WorksheetSortOrder({
  sheet,
  sheetPlan,
  change,
}: {
  sheet: WorkbookColumnarSheet;
  sheetPlan?: SheetPlan;
  change: ChangeSheet;
}) {
  const gt = useGT();
  const [moved, setMoved] = useState('');
  const sensors = useReorderSensors();
  function move(from: number, to: number) {
    const key = sheet.sort[from];
    if (!key || to < 0 || to >= sheet.sort.length) return;
    change((latest) => ({
      ...latest,
      sort: moveSortKey(latest.sort, from, to),
    }));
    setMoved(
      gt('Moved {column} to position {position}', {
        context: 'worksheet sort order reordered, for screen readers',
        column: key.column,
        position: String(to + 1),
      }),
    );
  }
  function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const ids = sheet.sort.map((key) => key.column);
    move(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  }
  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={sheet.sort.map((key) => key.column)}
          strategy={verticalListSortingStrategy}
        >
          <ol
            className="space-y-1"
            aria-label={gt('Sort keys', { context: 'worksheet sort order' })}
          >
            {sheet.sort.map((key, index) => (
              <SortKeyRow
                key={key.column}
                sheet={sheet}
                sheetPlan={sheetPlan}
                sortKey={key}
                index={index}
                move={move}
                change={change}
              />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
      <p role="status" className="sr-only">
        {moved}
      </p>
    </>
  );
}

function SortKeyRow({
  sheet,
  sheetPlan,
  sortKey,
  index,
  move,
  change,
}: {
  sheet: WorkbookColumnarSheet;
  sheetPlan?: SheetPlan;
  sortKey: SortKey;
  index: number;
  move: (from: number, to: number) => void;
  change: ChangeSheet;
}) {
  const gt = useGT();
  const sortLabel = useSortDirectionLabel();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: sortKey.column });
  const type = worksheetFieldType(sheet, sortKey.column, sheetPlan);
  const flipped = sortKey.direction === 'asc' ? 'desc' : 'asc';
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex items-center gap-1 rounded border bg-background p-1 text-xs"
    >
      <WorksheetReorderHandle
        sortable={{ attributes, listeners }}
        label={gt('Reorder {column}. Use the up and down arrow keys.', {
          context: 'worksheet sort key drag handle',
          column: sortKey.column,
        })}
        move={(delta) => move(index, index + delta)}
      />
      <span className="min-w-0 flex-1 truncate" title={sortKey.column}>
        <span className="text-muted-foreground">
          {index === 0
            ? gt('Sort by', { context: 'worksheet primary sort' })
            : gt('Then by', { context: 'worksheet secondary sort' })}{' '}
        </span>
        {sortKey.column}
      </span>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 px-1.5 text-xs"
        aria-label={gt('{column}: {order}. Change to {other}', {
          context: 'worksheet sort key flip direction',
          column: sortKey.column,
          order: sortLabel(type, sortKey.direction),
          other: sortLabel(type, flipped),
        })}
        onClick={() =>
          change((latest) => ({
            ...latest,
            sort: latest.sort.map((key) =>
              key.column === sortKey.column
                ? { ...key, direction: flipped }
                : key,
            ),
          }))
        }
      >
        {sortLabel(type, sortKey.direction)}
      </Button>
      <IconTooltip>
        <Button
          size="icon"
          variant="ghost"
          className="size-6"
          aria-label={gt('Remove sort by {column}', {
            context: 'worksheet remove sort',
            column: sortKey.column,
          })}
          onClick={() =>
            change((latest) => ({
              ...latest,
              sort: latest.sort.filter((key) => key.column !== sortKey.column),
            }))
          }
        >
          <TrashIcon />
        </Button>
      </IconTooltip>
    </li>
  );
}
